import React, { useCallback, useMemo } from "react";
import { Plus } from "lucide-react";
import { toast } from "react-toastify";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { FORM_COMPONENT_TYPES, LIMITS } from "../../utils/flowConstants";
import { createComponent } from "../../utils/flowJsonBuilder";
import ComponentEditor from "./ComponentEditor";

const SortableItem = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div ref={setNodeRef} style={style}>
      {children({ dragHandleProps: { ...attributes, ...listeners } })}
    </div>
  );
};

const FormEditor = ({ form, screens, currentScreenId, onUpdate }) => {
  const children = useMemo(() => form.children || [], [form.children]);
  const existingNames = children.map((c) => c.name).filter(Boolean);
  const formExpressionHints = existingNames.map((name) => `\${form.${name}}`);

  const isRichTextScreen = () => {
    const currentScreen = screens.find((s) => s.id === currentScreenId);
    if (!currentScreen) return false;
    const layoutChildren = currentScreen.layout?.children || [];
    return layoutChildren.some((child) => {
      if (child.type === "RichText") return true;
      if (child.type === "Form") {
        return (child.children || []).some((fc) => fc.type === "RichText");
      }
      return false;
    });
  };

  const hasNonRichOnScreen = () => {
    const currentScreen = screens.find((s) => s.id === currentScreenId);
    if (!currentScreen) return false;
    const layoutChildren = currentScreen.layout?.children || [];
    const types = [];
    layoutChildren.forEach((child) => {
      if (child.type === "Form") {
        (child.children || []).forEach((fc) => types.push(fc.type));
      } else {
        types.push(child.type);
      }
    });
    return types.some((t) => t && t !== "RichText");
  };

  const addComponent = useCallback(
    (type) => {
      const currentScreen = screens.find((s) => s.id === currentScreenId);
      if (currentScreen) {
        const layoutChildren = currentScreen.layout?.children || [];

        const typesOnScreen = [];
        let footerOutsideForm = false;
        layoutChildren.forEach((child) => {
          if (child.type === "Form") {
            (child.children || []).forEach((fc) => typesOnScreen.push(fc.type));
          } else {
            typesOnScreen.push(child.type);
            if (child.type === "Footer") footerOutsideForm = true;
          }
        });

        const hasRich = typesOnScreen.includes("RichText");
        const hasNonRich = typesOnScreen.some((t) => t && t !== "RichText");

        if (type === "RichText") {
          if (hasRich || hasNonRich) {
            toast.error("RichText screen can only contain RichText and nothing else. Remove other components first.");
            return;
          }
        } else if (hasRich) {
          toast.error("Cannot add this component because RichText is present on this screen. Remove RichText or use another screen.");
          return;
        }

        if (type === "Footer") {
          const footerInsideForm = children.some((c) => c.type === "Footer");
          if (footerOutsideForm) {
            toast.error("Footer already exists outside the Form. Remove it before adding a footer inside the form.");
            return;
          }
          if (footerInsideForm) {
            toast.error("Only one Footer is allowed inside a Form.");
            return;
          }
        }
      }

      const comp = createComponent(type, existingNames);
      onUpdate({ ...form, children: [...children, comp] });
    },
    [form, children, existingNames, onUpdate, screens, currentScreenId]
  );

  const updateComponent = useCallback(
    (idx, updated) => {
      const newChildren = children.map((c, i) => (i === idx ? updated : c));
      onUpdate({ ...form, children: newChildren });
    },
    [form, children, onUpdate]
  );

  const removeComponent = useCallback(
    (idx) => {
      onUpdate({ ...form, children: children.filter((_, i) => i !== idx) });
    },
    [form, children, onUpdate]
  );

  const moveComponent = useCallback(
    (idx, dir) => {
      const newIdx = idx + dir;
      if (newIdx < 0 || newIdx >= children.length) return;
      const arr = [...children];
      [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
      onUpdate({ ...form, children: arr });
    },
    [form, children, onUpdate]
  );

  const componentCount = children.length;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const sortableIds = useMemo(() => children.map((c) => c._uid || `idx_${children.indexOf(c)}`), [children]);

  const handleDragEnd = useCallback(
    (event) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIdx = sortableIds.indexOf(active.id);
      const newIdx = sortableIds.indexOf(over.id);
      if (oldIdx !== -1 && newIdx !== -1) {
        onUpdate({ ...form, children: arrayMove(children, oldIdx, newIdx) });
      }
    },
    [form, children, sortableIds, onUpdate]
  );

  return (
    <div className="waf-form">
      {/* Form name */}
      <div className="flex items-center gap-3">
        <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide shrink-0">
          Form Name
        </label>
        <input
          value={form.name || ""}
          onChange={(e) => onUpdate({ ...form, name: e.target.value })}
          className="flex-1 px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#048AAD]/40 bg-white font-mono"
          placeholder="form_name"
          maxLength={LIMITS.FORM_NAME_MAX_LEN}
        />
        <span
          className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
            componentCount > LIMITS.MAX_COMPONENTS_PER_FORM
              ? "bg-red-100 text-red-600"
              : "bg-gray-100 text-gray-500"
          }`}
        >
          {componentCount}/{LIMITS.MAX_COMPONENTS_PER_FORM}
        </span>
      </div>

      {formExpressionHints.length > 0 && (
        <div className="rounded-lg border border-blue-100 bg-blue-50/50 px-3 py-2">
          <p className="text-[10px] font-semibold text-blue-700 uppercase mb-1">Form Variables</p>
          <div className="flex flex-wrap gap-1">
            {formExpressionHints.map((hint) => (
              <span key={hint} className="text-[10px] font-mono px-1.5 py-0.5 bg-white border border-blue-100 rounded text-blue-700">
                {hint}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Components list */}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {children.map((comp, idx) => (
              <SortableItem key={comp._uid || idx} id={comp._uid || `idx_${idx}`}>
                {({ dragHandleProps }) => (
                  <ComponentEditor
                    component={comp}
                    index={idx}
                    total={children.length}
                    screens={screens}
                    currentScreenId={currentScreenId}
                    onUpdate={(updated) => updateComponent(idx, updated)}
                    onRemove={() => removeComponent(idx)}
                    onMove={(dir) => moveComponent(idx, dir)}
                    dragHandleProps={dragHandleProps}
                  />
                )}
              </SortableItem>
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {/* Add component palette */}
      {!isRichTextScreen() && componentCount < LIMITS.MAX_COMPONENTS_PER_FORM && (
        <div className="waf-form-add">
          <p>Add field</p>
          <div>
            {FORM_COMPONENT_TYPES.filter((ct) =>
              // If other components already exist on this screen, hide RichText option
              ct.value === "RichText" ? !hasNonRichOnScreen() : true
            ).map((ct) => (
              <button
                key={ct.value}
                onClick={() => addComponent(ct.value)}
                className="flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-medium bg-white border border-gray-200 rounded-lg hover:border-[#048AAD] hover:text-[#048AAD] transition-colors"
              >
                <Plus size={11} />
                {ct.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FormEditor;
